import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { LOGO_URL, COMPANY_NAME } from '@/config/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await register(email, password, name);
      if (result.pending) {
        setSuccess(true);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="border-slate-200 shadow-none max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Richiesta Inviata</h2>
            <p className="text-slate-500 mb-6">
              La tua richiesta di registrazione è stata inviata all'amministratore.
              Riceverai una notifica quando il tuo account sarà approvato.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                Torna al Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="bg-slate-900 rounded-lg p-2">
            <img src={LOGO_URL} alt="CR" className="h-10 w-auto object-contain" />
          </div>
          <span className="text-xl font-bold text-slate-900">{COMPANY_NAME}</span>
        </div>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold tracking-tight">Registrazione</CardTitle>
            <CardDescription className="text-slate-500">
              Richiedi l'accesso all'app. L'admin approverà il tuo account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-md" data-testid="register-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label>Nome Completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input type="text" placeholder="Mario Rossi" value={name} onChange={(e) => setName(e.target.value)} className="pl-10 border-slate-200" required data-testid="register-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input type="email" placeholder="nome@esempio.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 border-slate-200" required data-testid="register-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input type="password" placeholder="Minimo 6 caratteri" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 border-slate-200" required minLength={6} data-testid="register-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800 font-medium" disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Invio...' : 'Richiedi Accesso'}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-500">
              Hai già un account?{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">Accedi</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
